import { useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import { find } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import CurrentUserContext from '../CurrentUser';

import { ReportContext } from '.';

import { type Project, GetMyProjectsDocument, GetReportForSelectedDateDocument } from '@/graphql/generated/graphql';

interface ReportContextProviderProps {
  children: React.ReactNode;
}

const ReportContextProvider = ({ children }: ReportContextProviderProps) => {
  const { currentUser } = useContext(CurrentUserContext);
  const [searchParams] = useSearchParams();
  const [selectedProject, setSelectedProject] = useState<Project | undefined>(undefined);

  const { data } = useQuery(GetMyProjectsDocument, {
    skip: !currentUser,
    variables: {
      userId: currentUser?.id as string,
      companyId: currentUser?.defaultCompany?.id as string,
    },
  });

  useEffect(() => {
    if (data?.getMyProjects) {
      const queryProjectId = searchParams.get('projectId');
      const project = queryProjectId
        ? find(data.getMyProjects, (project) => project.id === queryProjectId)
        : data.getMyProjects[0];

      setSelectedProject(project as Project);
    }
  }, [data?.getMyProjects, searchParams]);

  const { data: selectedReport, refetch } = useQuery(GetReportForSelectedDateDocument, {
    skip: !selectedProject?.id,
    variables: {
      reportDate: searchParams.get('reportDate') ?? dayjs(new Date()).format('DD/MM/YYYY'),
      projectId: searchParams.get('projectId') ?? (selectedProject?.id as string),
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onUpdate = (value: any, key: string) => {
    switch (key) {
      case 'project':
        setSelectedProject(value);
        break;
      case 'date':
        refetch({
          reportDate: value,
          projectId: selectedProject?.id as string,
        });
        break;
    }
  };

  const isWeekendsEnabled = !!selectedProject?.projectSettings?.find(
    (setting) => setting.name === 'enable-weekends' && setting.value === 'true',
  );

  const projectShifts =
    find(selectedProject?.projectSettings, (setting) => setting.name === 'project-shifts')?.value || '';

  const projectHolidays =
    (selectedProject as any)?.holidays ||
    find(selectedProject?.projectSettings, (setting) => setting.name === 'project_holidays')?.value ||
    '';

  const context = {
    onUpdate,
    selectedProject: selectedProject,
    projects: data?.getMyProjects,
    selectedReport: selectedReport?.getReportForSubmission,
    isWeekendsEnabled: isWeekendsEnabled,
    projectShifts: projectShifts,
    projectHolidays: projectHolidays,
  };

  return <ReportContext.Provider value={context}>{children}</ReportContext.Provider>;
};
export default ReportContextProvider;
